import {AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {Select} from '@wba/commons/modules/wba-ui.module/components/formfields/select/select';
import {MitgliedDto} from '@wba/models/dto/mitglied-dto';
import {DateUtils} from '@wba/utils/date.utils';
import {debounceTime} from 'rxjs/operators';

const FORM_DEBOUNCE_TIME = 500;

export class FormUtils {

  static nullOrEmpty(value): boolean {
    return value === '' || value === null || typeof value === 'undefined';
  }

  static markFormGroupTouched(formGroup: UntypedFormGroup | UntypedFormArray) {

    const values: AbstractControl[] = Object.keys(formGroup?.controls).map(control => {
      return formGroup.controls[control];
    });
    values.forEach((control: UntypedFormGroup) => {
      control.markAsTouched();

      if (control.controls) {
        FormUtils.markFormGroupTouched(control);
      }
    });
  }

  static scrollToFirstInvalidField(controlName: string) {
    // timeout is required because .has-error class will be set with delay
    setTimeout(() => {
      let htmlElement: HTMLElement = <HTMLElement>document.querySelectorAll(
        `[formControlName="${controlName}"] .has-error .form-control`)[0];
      if (!htmlElement) {
        htmlElement = <HTMLElement>document.querySelectorAll(`[formControlName="${controlName}"] .form-control`)[0];
      }
      if (htmlElement) {
        htmlElement.focus({preventScroll: true});
        htmlElement.scrollIntoView({behavior: 'smooth', block: 'center', inline: 'center'});
      }
    });
  }

  static findInvalidControls(form: UntypedFormGroup): string[] {
    const invalid = [];
    const controls = form.controls;
    for (const name in controls) {
      if ((controls[name].invalid && controls[name].errors)) {
        if (controls[name].hasOwnProperty('controls')) {
          if (controls[name].validator) {
            invalid.push(Object.keys((controls[name] as UntypedFormGroup).controls)[0]);
          } else {
            FormUtils.findInvalidControls(controls[name] as UntypedFormGroup).forEach(controlName => invalid.push(controlName));
          }
        } else {
          invalid.push(name);
        }
      }
    }
    return invalid;
  }

  static findFirstInvalidControl(form: UntypedFormGroup): string {
    const controls = this.findInvalidControls(form);
    if (controls.length > 0) {
      return controls[0];
    }
    return null;
  }

  static createNachweisFormGroup(id, dateiname) {
    return new UntypedFormGroup({
      id: new UntypedFormControl(id),
      dateiname: new UntypedFormControl(dateiname),
      state: new UntypedFormControl(1)
    });
  }

  static createSelectItemsFromMitgliedObject(mitglieder: MitgliedDto[]): Select[] {
    return mitglieder.length < 1 ? [] : mitglieder.map(m => {
      return {
        value: m.id + '',
        label: `${m.vorname} ${m.nachname} (${DateUtils.format(m.geburtsdatum)})`
      };
    });
  }

  static clearValidatorsFromChildren(formGroup: UntypedFormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      formGroup.get(key).clearValidators();
    });
  }

  static resetFormArray(parentForm: UntypedFormGroup, formArrayName: string) {
    parentForm.setControl(formArrayName, new UntypedFormArray([]));
  }

  static formValueChangedDebounce(formValueChangeEmitter) {
    return formValueChangeEmitter.pipe(debounceTime(FORM_DEBOUNCE_TIME));
  }

  /* Form-Values verkürzen: führenden/nachfolgende Leerzeichen oder mehr als ein Leerzeichen hintereinander */
  static formValueSingleWhitespaces(formValue: string): string {
    return formValue.trim().replace(/[ ]{2,}/g, ' ');
  }

  static generiereName(name: string, i: number, k?: number) {
    return k != null ? name + '_' + i + '_' + k : name + '_' + i;
  }

  static indexFromName(name: string, index: number): number {
    const indizes = name.split('_');
    return Number.parseInt(indizes[index]);
  }
}
